import React, { useState, useEffect } from 'react';
import Chart from 'react-apexcharts';
import CONFIG from '../../config/config';
import { Card, CardBody, CardExpandToggler } from './../../components/card/card.jsx';

const Dashboard = () => {
  const [channels, setChannels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get the user role from localStorage instead of the Zustand store.
  const role = localStorage.getItem('role');

  useEffect(() => {
    const fetchChannels = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem('token');
        const response = await fetch(`${CONFIG.API_URL}/channel`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error('Failed to fetch channels');
        }
        const data = await response.json();
        // Adjust the property name based on your API response structure.
        setChannels(data.channels || []);
      } catch (err) {
        setError(err.message || 'Something went wrong while fetching channels.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchChannels();
  }, []);

  // Helper to aggregate engagement stats from videos
  const aggregateEngagement = (videos, key) =>
    videos.reduce((acc, video) => acc + (parseInt(video[key], 10) || 0), 0);

  if (isLoading) return <div className="text-center mt-5">Loading...</div>;
  if (error) return <div className="alert alert-danger mt-5">{error}</div>;

  return (
    <div className="row">
      {channels.map((channel) => (
        <div className="col-xl-6 col-lg-12" key={channel._id}>
          {channel.youtube && channel.youtube.channelId ? (
            <Card className="mb-3">
              <CardBody>
                <div className="d-flex fw-bold small mb-3">
                  <span className="flex-grow-1">
                    {channel.name} – YouTube Stats
                  </span>
                  <CardExpandToggler />
                </div>

                {/* Subscribers Card */}
                <div className="mb-3">
                  <h5>Subscribers: {channel.youtube.subscribers}</h5>
                </div>

                {/* Only show video-related charts if videos exist */}
                {channel.youtube.videos && channel.youtube.videos.length > 0 ? (
                  <>
                    {/* For admin: show a line chart of video views */}
                    {role === 'admin' && (
                      <div className="mb-3">
                        <Chart
                          type="line"
                          height="300"
                          options={{
                            chart: { id: 'video-views' },
                            xaxis: {
                              // Use a short version of the video ID for labels
                              categories: channel.youtube.videos.map((v) =>
                                v.videoId.substring(0, 6)
                              ),
                            },
                            title: { text: 'Video Views', align: 'center' },
                            stroke: { curve: 'smooth', width: 2 },
                            markers: { size: 5 },
                          }}
                          series={[
                            {
                              name: 'Views',
                              data: channel.youtube.videos.map((v) =>
                                parseInt(v.views, 10) || 0
                              ),
                            },
                          ]}
                        />
                      </div>
                    )}

                    {/* Engagement Donut Chart (aggregates likes, comments, dislikes) */}
                    <div className="mb-3">
                      <Chart
                        type="donut"
                        height="250"
                        options={{
                          labels: ['Likes', 'Comments', 'Dislikes'],
                          title: { text: 'Engagement', align: 'center' },
                        }}
                        series={[
                          aggregateEngagement(channel.youtube.videos, 'likes'),
                          aggregateEngagement(channel.youtube.videos, 'comments'),
                          aggregateEngagement(channel.youtube.videos, 'dislikes'),
                        ]}
                      />
                    </div>

                    {/* For editor: show a table with individual video stats */}
                    {role === 'editor' && (
                      <div>
                        <h6>Videos</h6>
                        <table className="table table-striped">
                          <thead>
                            <tr>
                              <th>Video ID</th>
                              <th>Views</th>
                              <th>Likes</th>
                              <th>Comments</th>
                            </tr>
                          </thead>
                          <tbody>
                            {channel.youtube.videos.map((video, idx) => (
                              <tr key={idx}>
                                <td>{video.videoId}</td>
                                <td>{video.views}</td>
                                <td>{video.likes}</td>
                                <td>{video.comments}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                ) : (
                  <div>No video data available.</div>
                )}
              </CardBody>
            </Card>
          ) : (
            // Render this card if no YouTube data is present for the channel
            <Card className="mb-3">
              <CardBody>
                <div>No data available for the channel yet</div>
              </CardBody>
            </Card>
          )}
        </div>
      ))}
    </div>
  );
};

export default Dashboard;
